import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  NativeSelect,
  NativeSelectOption,
} from "@/components/ui/native-select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { errorText } from "@/components/forms/form-errors";
import { money } from "@/pages/jobs/types";

export type EditorLine = {
  id: string;
  name: string;
  quantity: string;
  unit_price: string;
  discount_amount: string;
  discount_percentage: string;
  line_id?: string;
  line_revision?: string;
  offering_id?: string;
  offering_revision?: string;
  service_revision?: string;
  tax_mode?: string;
  tax_rate?: string;
  currency?: string;
};
const minor = (value: string) => {
  if (!/^\d{1,10}([.,]\d{1,2})?$/.test(value.trim()))
    throw new Error("invalid");
  const [whole, fraction = ""] = value.trim().replace(",", ".").split(".");
  return BigInt(whole) * 100n + BigInt(fraction.padEnd(2, "0"));
};
const round = (a: bigint, b: bigint) => (a * 2n + b) / (b * 2n);
const discount = (base: bigint, fixed: string, percentage: string) => {
  const amount = minor(fixed || "0"),
    rate = minor(percentage || "0");
  if (rate > 10000n || (amount > 0n && rate > 0n)) throw new Error("invalid");
  const value = amount + round(base * rate, 10000n);
  if (value > base) throw new Error("invalid");
  return value;
};
export function estimateServiceTotal(
  lines: EditorLine[],
  fixed: string,
  percentage: string,
): bigint | null {
  try {
    if (!lines.length) return null;
    const amounts = lines.map((line) => {
      if (
        !/^\d{1,5}$/.test(line.quantity) ||
        Number(line.quantity) < 1 ||
        Number(line.quantity) > 10000
      )
        throw new Error("invalid");
      const base = minor(line.unit_price) * BigInt(line.quantity);
      return (
        base - discount(base, line.discount_amount, line.discount_percentage)
      );
    });
    const base = amounts.reduce((a, b) => a + b, 0n),
      reduction = discount(base, fixed, percentage);
    const allocations = amounts.map((value) =>
      base ? (value * reduction) / base : 0n,
    );
    const order = lines
      .map((_, i) => i)
      .sort((a, b) => {
        const ar = base ? (amounts[a] * reduction) % base : 0n,
          br = base ? (amounts[b] * reduction) % base : 0n;
        return ar === br
          ? lines[a].id.localeCompare(lines[b].id)
          : ar > br
            ? -1
            : 1;
      });
    const remainder = Number(
      reduction - allocations.reduce((a, b) => a + b, 0n),
    );
    order.slice(0, remainder).forEach((index) => allocations[index]++);
    return lines.reduce((sum, line, index) => {
      const amount = amounts[index] - allocations[index];
      const rate = BigInt(Math.round(Number(line.tax_rate || "0") * 10000));
      return (
        sum +
        amount +
        (line.tax_mode === "exclusive" ? round(amount * rate, 1000000n) : 0n)
      );
    }, 0n);
  } catch {
    return null;
  }
}
export const decimal = (value: number | string) => {
  const amount = BigInt(value);
  return `${amount / 100n}.${String(amount % 100n).padStart(2, "0")}`;
};
export const linePayload = (line: EditorLine) => ({
  selected: "1",
  ...Object.fromEntries(
    Object.entries(line).filter(
      ([key]) =>
        !["id", "name", "tax_mode", "tax_rate", "currency"].includes(key),
    ),
  ),
});
export function DiscountField({
  id,
  amount,
  percentage,
  onChange,
  percentageEnabled = true,
}: {
  id: string;
  amount: string;
  percentage: string;
  percentageEnabled?: boolean;
  onChange: (amount: string, percentage: string) => void;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState(
    percentage && percentage !== "0" && percentage !== "0.00"
      ? "percentage"
      : amount && amount !== "0" && amount !== "0.00"
        ? "amount"
        : "none",
  );
  return (
    <div className="space-y-2">
      <Label htmlFor={`${id}-mode`}>{t("proposals.discount")}</Label>
      <NativeSelect
        id={`${id}-mode`}
        value={mode}
        onChange={(event) => {
          setMode(event.target.value);
          onChange("0", "0");
        }}
        className="w-full"
      >
        {(percentageEnabled
          ? ["none", "amount", "percentage"]
          : ["none", "amount"]
        ).map((value) => (
          <NativeSelectOption key={value} value={value}>
            {t(`workflow.discount_${value}`)}
          </NativeSelectOption>
        ))}
      </NativeSelect>
      {mode !== "none" && (
        <>
          <Label htmlFor={id}>
            {t(
              mode === "percentage"
                ? "proposals.discount_percentage"
                : "proposals.discount_amount",
            )}
          </Label>
          <Input
            id={id}
            inputMode="decimal"
            value={mode === "percentage" ? percentage : amount}
            onChange={(event) =>
              onChange(
                mode === "amount" ? event.target.value : "0",
                mode === "percentage" ? event.target.value : "0",
              )
            }
          />
        </>
      )}
    </div>
  );
}
export function ServiceEditor({
  lines,
  options,
  onChange,
  idPrefix,
  errors = {},
}: {
  errors?: Record<string, unknown>;
  lines: EditorLine[];
  options: EditorLine[];
  onChange: (value: EditorLine[]) => void;
  idPrefix: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false),
    [query, setQuery] = useState("");
  const trigger = useRef<HTMLButtonElement>(null);
  const available = options.filter(
    (option) =>
      !lines.some((line) => line.id === option.id) &&
      option.name.toLocaleLowerCase().includes(query.toLocaleLowerCase()),
  );
  const update = (index: number, values: Partial<EditorLine>) =>
    onChange(
      lines.map((line, i) => (i === index ? { ...line, ...values } : line)),
    );
  return (
    <section
      id={`${idPrefix}-services`}
      tabIndex={-1}
      className="min-w-0 space-y-4"
      aria-label={t("jobs.services")}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">{t("jobs.services")}</h2>
        <Button
          ref={trigger}
          type="button"
          variant="outline"
          onClick={() => setOpen(true)}
        >
          {t("workflow.add_service")}
        </Button>
      </div>
      {!lines.length && (
        <p className="text-sm text-muted-foreground">
          {t("workflow.choose_services")}
        </p>
      )}
      {lines.map((line, index) => {
        const current = options.find((option) => option.id === line.id);
        const changed =
          !line.line_id &&
          (!current ||
            current.offering_revision !== line.offering_revision ||
            current.service_revision !== line.service_revision);
        return (
          <fieldset key={line.id} className="space-y-4 rounded-lg border p-4">
            <legend className="px-1 font-medium">{line.name}</legend>
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                {line.tax_rate || "0"}% ·{" "}
                {t(`proposals.${line.tax_mode || "exclusive"}`)}
              </p>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onChange(lines.filter((_, i) => i !== index))}
                aria-label={`${t("workflow.remove")} ${line.name}`}
              >
                {t("workflow.remove")}
              </Button>
            </div>
            {changed && (
              <div className="space-y-3 rounded-md border p-3" role="alert">
                <p>
                  {t(
                    current
                      ? "workflow.changed_service"
                      : "workflow.unavailable_service",
                  )}
                </p>
                {current && (
                  <>
                    <p>
                      {current.name} · {current.unit_price} {current.currency} ·{" "}
                      {current.tax_rate}%
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        update(index, {
                          ...current,
                          quantity: line.quantity,
                          unit_price: line.unit_price,
                          discount_amount: line.discount_amount,
                          discount_percentage: line.discount_percentage,
                        })
                      }
                    >
                      {t("workflow.review_service")}
                    </Button>
                  </>
                )}
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor={`${idPrefix}-quantity-${index}`}>
                  {t("proposals.quantity")}
                </Label>
                <Input
                  id={`${idPrefix}-quantity-${index}`}
                  inputMode="numeric"
                  aria-invalid={
                    lines.length === 1 && index === 0 && !!errors.quantity
                  }
                  aria-describedby={
                    lines.length === 1 && index === 0 && errors.quantity
                      ? `${idPrefix}-quantity-error`
                      : undefined
                  }
                  value={line.quantity}
                  onChange={(event) =>
                    update(index, { quantity: event.target.value })
                  }
                />
                {lines.length === 1 && index === 0 && errors.quantity ? (
                  <p
                    id={`${idPrefix}-quantity-error`}
                    className="text-sm text-destructive"
                  >
                    {errorText(errors.quantity)}
                  </p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor={`${idPrefix}-price-${index}`}>
                  {t("proposals.unit_price")}
                </Label>
                <Input
                  id={`${idPrefix}-price-${index}`}
                  inputMode="decimal"
                  aria-invalid={
                    lines.length === 1 &&
                    index === 0 &&
                    !!(errors.unit_price || errors.unit_price_minor)
                  }
                  value={line.unit_price}
                  onChange={(event) =>
                    update(index, { unit_price: event.target.value })
                  }
                />
                {lines.length === 1 &&
                index === 0 &&
                (errors.unit_price || errors.unit_price_minor) ? (
                  <p className="text-sm text-destructive">
                    {errorText(errors.unit_price || errors.unit_price_minor)}
                  </p>
                ) : null}
              </div>
            </div>
            <details>
              <summary className="cursor-pointer py-2 text-sm">
                {t("workflow.line_discount")}
              </summary>
              <DiscountField
                key={line.id}
                id={`${idPrefix}-discount-${index}`}
                amount={line.discount_amount}
                percentage={line.discount_percentage}
                onChange={(amount, percentage) =>
                  update(index, {
                    discount_amount: amount,
                    discount_percentage: percentage,
                  })
                }
              />
            </details>
          </fieldset>
        );
      })}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          className="w-full! overflow-y-auto sm:max-w-lg!"
          finalFocus={trigger}
        >
          <SheetHeader>
            <SheetTitle>{t("workflow.add_service")}</SheetTitle>
            <SheetDescription>{t("workflow.choose_services")}</SheetDescription>
          </SheetHeader>
          <div className="space-y-4 px-4 pb-4">
            <Label htmlFor={`${idPrefix}-service-search`}>
              {t("requests.search")}
            </Label>
            <Input
              autoFocus
              id={`${idPrefix}-service-search`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            {!available.length && (
              <p role="status" className="text-sm text-muted-foreground">
                {t("workflow.no_services")}
              </p>
            )}
            <ul className="divide-y">
              {available.map((option) => (
                <li
                  key={option.id}
                  className="flex items-center justify-between gap-3 py-4"
                >
                  <div className="min-w-0">
                    <p className="font-medium break-words">{option.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {option.unit_price} {option.currency}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      onChange([...lines, option]);
                      setOpen(false);
                    }}
                    aria-label={`${t("workflow.add")} ${option.name}`}
                  >
                    {t("workflow.add")}
                  </Button>
                </li>
              ))}
            </ul>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              {t("common.cancel")}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </section>
  );
}
export function serviceCurrency(lines: EditorLine[]): string | null {
  const currencies = new Set(lines.map((line) => line.currency));
  const currency = lines[0]?.currency;
  return currencies.size === 1 && currency && /^[A-Z]{3}$/.test(currency)
    ? currency
    : null;
}
export function TotalsSummary({
  lines,
  amount,
  percentage,
  children,
  help,
}: {
  lines: EditorLine[];
  amount: string;
  percentage: string;
  children?: React.ReactNode;
  help?: React.ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const total = estimateServiceTotal(lines, amount, percentage);
  const currency = serviceCurrency(lines);
  return (
    <aside
      className="h-fit space-y-5 rounded-lg border bg-card p-5 xl:sticky xl:top-5"
      aria-label={t("workflow.estimate")}
    >
      <div>
        <h2 className="font-semibold">{t("workflow.estimate")}</h2>
        <p className="mt-2 text-2xl font-semibold" data-commercial-estimate>
          {total === null || !currency
            ? "—"
            : money(total.toString(), currency, i18n.language)}
        </p>
        {!currency && lines.length > 0 && (
          <p role="status" className="mt-2 text-sm">
            {t("workflow.estimate_unavailable")}
          </p>
        )}
        <p className="mt-2 text-sm text-muted-foreground">
          {help ?? t("workflow.estimate_help")}
        </p>
      </div>
      {children}
    </aside>
  );
}
export function changedServices(lines: EditorLine[], options: EditorLine[]) {
  return lines.some(
    (line) =>
      !line.line_id &&
      !options.some(
        (current) =>
          current.id === line.id &&
          current.offering_revision === line.offering_revision &&
          current.service_revision === line.service_revision,
      ),
  );
}
